import React, { Fragment } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import InputLabel from "components/InputLabel";

interface ComboboxSelectProps<T> extends React.HTMLProps<HTMLInputElement> {
  items: T[];
  displayKey: keyof T;
  detailKey?: keyof T;
  descriptionKey?: keyof T;
  handleChange?: (value: T, name?: string) => void;
  optionsClassName?: string;
  height?: "small" | "large" | "middle";
  disabled?: boolean;
  containerClassName?: string;
  selectedOption?: T;
  labelName?: string;
  required?: boolean;
}

export default function ComboboxSelect<Item extends object>(
  props: ComboboxSelectProps<Item>
) {
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const [selectedItem, setSelectedItem] = React.useState<
    Item | null | undefined
  >(null);
  const optionsListRef = React.useRef<HTMLUListElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  function onArrowDown() {
    setFocusedIndex((prev) =>
      prev === props.items.length - 1 ? prev : prev + 1
    );
  }

  function onArrowUp() {
    setFocusedIndex((prev) => (prev <= 0 ? -1 : Number(prev) - 1));
  }

  function onOptionEnter() {
    handleOnClick(props.items[focusedIndex]);
  }

  const handleOnClick = (item: Item) => {
    setSelectedItem(item);
    item && props.handleChange?.(item);
  };

  React.useEffect(() => {
    setSelectedItem(props.selectedOption);
  }, [props.selectedOption]);

  function focusToOptionsList() {
    setTimeout(() => {
      optionsListRef.current?.focus();
      optionsListRef.current?.scrollTo({ top: 0 });
    }, 1);
  }

  function handleScrollList(e: React.KeyboardEvent) {
    e.stopPropagation();
    if (e.code === "ArrowDown") {
      onArrowDown();
    } else if (e.code === "ArrowUp") {
      onArrowUp();
      focusedIndex === -1 && inputRef.current?.focus();
    } else if (e.code === "Enter") {
      onOptionEnter();
    } else {
      inputRef.current?.focus();
    }
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    e.stopPropagation();
    if (e.key === "Enter" || e.key === "Escape") {
      const shouldEnterQuery = focusedIndex === -1 || props.items.length === 0;
      !shouldEnterQuery && onOptionEnter();
    } else if (e.code === "ArrowDown") {
      e.preventDefault();
      focusToOptionsList();
      onArrowDown();
    } else if (e.code === "ArrowUp") {
      e.preventDefault();
      focusToOptionsList();
      onArrowUp();
    }
  }

  const defaultValue = selectedItem
    ? (selectedItem[props.displayKey] as string)
    : "";

  return (
    <div className={props.containerClassName}>
      {props.labelName ? (
        <InputLabel
          required={props.required}
          labelName={props.labelName ?? ""}
        />
      ) : null}
      <Combobox value={selectedItem} onChange={setSelectedItem}>
        <div className="relative">
          <div
            className={clsx(
              "relative w-full cursor-pointer overflow-hidden rounded-md font-medium text-sm border-gray-300",
              "border text-left h-large focus-within:border-indigo-500",
              {
                "h-large": props.height === "large",
                "!h-middle": props.height === "middle",
                "!h-small": props.height === "small",
              }
            )}
          >
            <Combobox.Button
              className={clsx(
                "flex justify-between w-full h-full inset-y-0 right-0 items-center px-2",
                "text-gray-700",
                {
                  "h-large": props.height === "large",
                  "!h-middle": props.height === "middle",
                  "!h-small": props.height === "small",
                }
              )}
            >
              {({ open }) => (
                <>
                  <Combobox.Input
                    className={clsx(
                      "w-full pl-1 text-sm leading-5 ring-0 outline-none border-none focus:ring-0",
                      "rounded-md pointer-events-none !cursor-default placeholder:text-gray-500",
                      "placeholder:text-sm placeholder:font-normal"
                    )}
                    ref={inputRef}
                    placeholder={props.placeholder}
                    onKeyDown={handleSearchKeyDown}
                    displayValue={() => defaultValue}
                    readOnly
                  />
                  <IconChevronDown
                    className={clsx(
                      "min-h-[18px] min-w-[18px] text-gray-600",
                      open ? "rotate-180" : ""
                    )}
                    size={18}
                    aria-hidden="true"
                  />
                </>
              )}
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setFocusedIndex(-1)}
          >
            <Combobox.Options
              onKeyDown={handleScrollList}
              className={clsx(
                "absolute z-10 my-1 max-h-60 w-full overflow-auto rounded-md bg-white text-base !p-0 !max-w-fit",
                "shadow-lg border border-gray-300 min-w-auto w-auto max-w-md focus:outline-none sm:text-sm",
                props.optionsClassName,
                { "cursor-not-allowed": props.disabled }
              )}
            >
              {props.items?.length === 0 ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700"></div>
              ) : (
                props.items?.map((item, index) => (
                  <Combobox.Option
                    key={index}
                    className={() =>
                      clsx(
                        "group/dropdownItem text-gray-700 hover:bg-indigo-50 py-2 px-4 hover:text-indigo-700",
                        props.disabled
                          ? "cursor-not-allowed"
                          : "cursor-pointer",
                        {
                          "bg-indigo-50 text-indigo-700":
                            focusedIndex === index,
                        }
                      )
                    }
                    value={item}
                    onClick={() => handleOnClick(item)}
                  >
                    {({ selected }) => (
                      <div>
                        <div className="flex justify-between items-center w-full">
                          <span
                            className={`block truncate ${
                              selected ? "font-medium" : "font-normal"
                            }`}
                          >
                            {props.displayKey in item
                              ? (item[props.displayKey] as unknown as string)
                              : null}
                          </span>
                          <span className="text-gray-500 group-hover/dropdownItem:text-indigo-500 ml-6">
                            {(item[props.detailKey as keyof Item] as string) ??
                              ""}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 group-hover/dropdownItem:text-indigo-500">
                          {(item[
                            props.descriptionKey as keyof Item
                          ] as string) ?? ""}
                        </div>
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
